import { HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { MerchantContextService } from "app/core/auth/services";
import { TenantAuthConfigService } from "app/core/services/tenant-auth-config.service";
import { environment } from "environments/environment";
import { KeycloakService } from "keycloak-angular";
import { EMPTY, from, switchMap } from "rxjs";

// Reactivo: justo antes de cada request (distinto del proactivo cada 30s de
// TokenRefreshService, que corre en segundo plano sin depender de que haya tráfico).
const UPDATE_TOKEN_MIN_VALIDITY_SECONDS = 30;

/**
 * Agrega Authorization/X-Tenant/X-Selected-Merchant solo a requests hacia
 * hosts propios (API_HOSTS). El filtro por host es una medida de seguridad,
 * no de optimización: nunca debe viajar un Bearer a un dominio de terceros.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const isOwnApiHost = environment.API_HOSTS.some((host) =>
    req.url.startsWith(host),
  );

  if (!isOwnApiHost) {
    return next(req);
  }

  const keycloakService = inject(KeycloakService);
  const merchantContext = inject(MerchantContextService);
  const tenantConfig = inject(TenantAuthConfigService);

  return from(
    keycloakService.updateToken(UPDATE_TOKEN_MIN_VALIDITY_SECONDS),
  ).pipe(
    switchMap(() => {
      const token = keycloakService.getKeycloakInstance().token;

      // keycloak-angular NO propaga el fallo de updateToken(): atrapa el error
      // y devuelve false (ver KeycloakService.updateToken). Cuando el refresh
      // falla de verdad, keycloak-js ya limpió el token, así que sin este
      // chequeo el header saldría literalmente como "Bearer undefined" y el
      // backend respondería un 401 indistinguible de una sesión vencida.
      if (!token) {
        keycloakService.login({ redirectUri: window.location.href });
        return EMPTY;
      }

      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        // Para observabilidad/auditoría en backend, no reemplaza la validación real del JWT.
        "X-Tenant": tenantConfig.clientId(),
      };

      const selectedMerchantB64 = merchantContext.getSelectedMerchantB64();
      if (selectedMerchantB64) {
        headers["X-Selected-Merchant"] = selectedMerchantB64;
      }

      return next(req.clone({ setHeaders: headers }));
    }),
  );
};
